import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponseBase } from '@angular/common/http';
import { PayrollTaskSearchConfig, SubTask, Task, TaskSearchConfig } from '../models/task';
import { Observable } from 'rxjs';
import { PagedPayload } from '../models/paged-payload';
import { IdNamePair } from '../models/id-name-pair';
import { AuthService } from './auth.service';
import { PayrollInfo } from '../models/payroll-info';
import { MasterPayrollObject } from '../models/payroll';

@Injectable({
  providedIn: 'root'
})
export class TaskService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    @Inject('env') private env: any
  ) {}

  public createTask(task: Task): Observable<number> {
    return this.http.post<number>(`${this.env.apiUrlRoot}/Tasks`, task);
  }

  public deleteTask(taskId: number) {
    return this.http.delete<HttpResponseBase>(`${this.env.apiUrlRoot}/Tasks/${taskId}`);
  }

  public getLastViewed(): Observable<Record<number, Date>> {
    return this.http.get<Record<number, Date>>(
      `${this.env.apiUrlRoot}/Tasks/LastTicketViews/${this.authService.getCurrentUserName()}`
    );
  }

  public getNonPayrollTaskCount(searchConfig: TaskSearchConfig) {
    return this.http.post<any>(`${this.env.apiUrlRoot}/Tasks/NonPayrollTaskCounts`, searchConfig);
  }

  public getNonPayrollTasks(searchConfig: TaskSearchConfig): Observable<PagedPayload<Task>> {
    return this.http.post<PagedPayload<Task>>(
      `${this.env.apiUrlRoot}/Tasks/FindNonPayrollTasks`,
      searchConfig
    );
  }

  public getPayrollTaskCount(searchConfig: PayrollTaskSearchConfig) {
    return this.http.post<any>(`${this.env.apiUrlRoot}/Tasks/PayrollTaskCounts`, searchConfig);
  }

  public getPayrollTasks(searchConfig: PayrollTaskSearchConfig): Observable<PagedPayload<Task>> {
    return this.http.post<PagedPayload<Task>>(
      `${this.env.apiUrlRoot}/Tasks/FindPayrollTasks`,
      searchConfig
    );
  }

  public getTaskTypes(): Observable<IdNamePair[]> {
    return this.http.get<IdNamePair[]>(`${this.env.apiUrlRoot}/Tasks/GetTaskTypes`);
  }

  public setLastTicketView(taskId: number) {
    const userId = this.authService.getCurrentUserName();
    return this.http.post<HttpResponseBase>(
      `${this.env.apiUrlRoot}/Tasks/${taskId}/SetLastTicketView/${userId}`,
      {}
    );
  }

  public updateTask(task: Task): Observable<number> {
    return this.http.put<number>(`${this.env.apiUrlRoot}/Tasks/${task.Id}`, task);
  }

  // task 7528 - when a regular payroll is cancelled, update the FD ticket
  public updateTicketCancelPayroll(payroll: MasterPayrollObject) {
    return this.http.put(`${this.env.apiUrlRoot}/Tasks/UpdateTicketCancelPayroll`, payroll);
  }

  // #region subtasks

  public getSubTasks(taskId: number): Observable<SubTask[]> {
    return this.http.get<SubTask[]>(`${this.env.apiUrlRoot}/Tasks/${taskId}/SubTasks`);
  }

  public markSubTaskAsComplete(taskId: number, subTaskId: number): Observable<HttpResponseBase> {
    return this.http.post<HttpResponseBase>(
      `${this.env.apiUrlRoot}/Tasks/${taskId}/SubTasks/${subTaskId}/Complete`,
      {}
    );
  }

  public markSubTaskAsIncomplete(taskId: number, subTaskId: number): Observable<HttpResponseBase> {
    return this.http.delete<HttpResponseBase>(
      `${this.env.apiUrlRoot}/Tasks/${taskId}/SubTasks/${subTaskId}`
    );
  }

  // #endregion
}
